'use client';
import { useCallback, useRef, useState, MouseEvent, TouchEvent } from 'react';

const UseLongPress = (
    onLongPress: (event: MouseEvent<any> | TouchEvent<any>) => void,
    onClick: (event: MouseEvent<any> | TouchEvent<any>) => void,
    { shouldPreventDefault = true, delay = 1000 } = {},
) => {
    const [longPressTriggered, setLongPressTriggered] = useState(false);
    const timeout = useRef<NodeJS.Timeout>();
    const target = useRef<EventTarget>();
    const originalPosition = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
    const startTime = useRef<number>(0);

    const start = useCallback(
        (event: MouseEvent | TouchEvent) => {
            if (shouldPreventDefault && event.target) {
                event.target.addEventListener('touchend', preventDefault, {
                    passive: false,
                });
                target.current = event.target;
            }
            const position = event.currentTarget.getBoundingClientRect();
            originalPosition.current = { x: position.left, y: position.top };
            startTime.current = Date.now();
            timeout.current = setTimeout(() => {
                onLongPress(event);
                setLongPressTriggered(true);
            }, delay);
        },
        [onLongPress, delay, shouldPreventDefault],
    );

    const clear = useCallback(
        (event: MouseEvent | TouchEvent, shouldTriggerClick = true) => {
            if (timeout.current) clearTimeout(timeout.current);
            const currentPosition = event.currentTarget.getBoundingClientRect();
            const diff = Math.sqrt(
                Math.pow(currentPosition.left - originalPosition.current.x, 2) +
                    Math.pow(currentPosition.top - originalPosition.current.y, 2),
            );
            const timeDiff = Date.now() - startTime.current;
            if (shouldTriggerClick && !longPressTriggered && diff < 10 && timeDiff < 1000 && timeDiff < 850)
                onClick(event);
            setLongPressTriggered(false);
            if (shouldPreventDefault && target.current) {
                target.current.removeEventListener('touchend', preventDefault);
            }
        },
        [shouldPreventDefault, onClick, longPressTriggered],
    );

    return {
        onMouseDown: (e: MouseEvent) => start(e),
        onTouchStart: (e: TouchEvent) => start(e),
        onMouseUp: (e: MouseEvent) => clear(e),
        onMouseLeave: (e: MouseEvent) => clear(e, false),
        onTouchEnd: (e: TouchEvent) => clear(e),
    };
};

const isTouchEvent = (event: Event | TouchEvent): event is TouchEvent => {
    return 'touches' in event;
};

const preventDefault = (event: Event | TouchEvent) => {
    if (!isTouchEvent(event)) return;
    if (event.touches.length < 2 && event.preventDefault) {
        event.preventDefault();
    }
};

export default UseLongPress;
